import type { CoachingNote, FeedbackReview, MatchSummary } from "@/lib/types";

/**
 * Pure helpers behind the `/feedback` viewer surfaces.
 *
 * Extracted from the tab components so the non-trivial bits get
 * Vitest coverage without a render harness:
 *   - `summarySections` — which match-summary recap fields show,
 *     in order (the Match Summaries card preview + full view).
 *   - `reviewedNoteIds` / `reviewedPlaylistIds` — derive the
 *     reviewed-id sets from a `/feedback` bundle's `reviews[]`
 *     (drives the Reviewed/New pills).
 *   - `noteDisplayTitle` — the observation-aware title fallback
 *     chain for a viewer note card.
 */

/** The recap fields a match summary can carry, in display order.
 * `summarySections` keeps only the non-empty ones. */
const SUMMARY_FIELDS: ReadonlyArray<[keyof MatchSummary, string]> = [
  ["team_positives", "What went well"],
  ["team_improvements", "What we can improve"],
  ["training_focus", "Training focus"],
  ["body", "Coach recap"],
];

/** `[label, value]` pairs for a match summary's non-empty recap
 * fields, in display order. Ports legacy `_summarySectionData`. */
export function summarySections(s: MatchSummary): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [key, label] of SUMMARY_FIELDS) {
    const value = (s[key] as string | undefined)?.trim();
    if (value) out.push([label, value]);
  }
  return out;
}

/** The set of note ids the viewer has marked reviewed. */
export function reviewedNoteIds(reviews: FeedbackReview[]): Set<number> {
  return new Set(
    reviews
      .map((r) => r.note_id)
      .filter((id): id is number => typeof id === "number"),
  );
}

/** The set of playlist ids the viewer has marked reviewed. */
export function reviewedPlaylistIds(
  reviews: FeedbackReview[],
): Set<number> {
  return new Set(
    reviews
      .map((r) => r.playlist_id)
      .filter((id): id is number => typeof id === "number"),
  );
}

/** A viewer note card's display title — handles the observation
 * fallback chain (`title` → `event_title` → "Observation note";
 * video notes fall back to "(untitled)"). */
export function noteDisplayTitle(n: CoachingNote): string {
  const isObservation = (n.note_context ?? "video") === "observation";
  return (
    n.title?.trim() ||
    (isObservation
      ? n.event_title?.trim() || "Observation note"
      : "(untitled)")
  );
}
